import type { Meta, ProfileViewModel, SubmitVouchModel } from "@/lib/types";
import { OATH_KEY } from "@/lib/vouch-flow";
import { getOtp, submitVouch } from "@/services";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router";
import { useSessionStorage } from "./use-session-storage";

export const useVerifyVouch = () => {
  const navigate = useNavigate();
  const { vouchId } = useParams<{ vouchId: string }>();
  const { state } = useLocation();
  const profile = useOutletContext<ProfileViewModel>();
  const queryClient = useQueryClient();
  const { getSessionStore, clearSessionStore } = useSessionStorage();

  const session = getSessionStore<Partial<Meta> & { Phone?: string }>(
    vouchId!,
    {},
  );
  const { Phone: storedPhone, ...storedMeta } = session;
  const phone: string | undefined = state?.Phone ?? storedPhone;
  const meta: Meta | undefined = state?.meta ?? (storedMeta as Meta);

  const [otpError, setOtpError] = useState<string | undefined>();
  const otpRef = useRef<HTMLInputElement>(null);

  const resendMutation = useMutation({
    mutationFn: () => getOtp(phone!),
  });

  const submitMutation = useMutation({
    mutationFn: (otp: string) => {
      const payload: SubmitVouchModel = {
        VouchId: vouchId!,
        OTP: otp,
        Phone: phone,
        Meta: meta!,
      };
      return submitVouch(payload);
    },
    onSuccess: (data) => {
      if (data.ApiHttpResponse === 202) {
        clearSessionStore(vouchId!);
        clearSessionStore(OATH_KEY(vouchId!));
        navigate("../complete", {
          state: {
            vouchee: { firstName: profile?.FirstName, gender: profile?.Gender },
            voucher: { meta },
          },
        });
        queryClient.invalidateQueries({ queryKey: ["vouch-session"] });
      } else {
        setOtpError(data.PublicMessage ?? "Invalid code. Please try again.");
        otpRef.current?.focus();
      }
    },
    onError: () => {
      setOtpError("Something went wrong. Please try again.");
      otpRef.current?.focus();
    },
  });

  const handleComplete = (finalValue: string) => {
    setOtpError(undefined);
    submitMutation.mutate(finalValue);
  };

  const resendOtp = () => resendMutation.mutate();

  return {
    phone,
    otpError,
    otpRef,
    handleComplete,
    resendOtp,
    isSubmitting: submitMutation.isPending,
    isResending: resendMutation.isPending,
  };
};
